import type { DemoAccessAssignment } from "@/store/api/baseApi";
import { DEMO_USAGE_ORG_ID } from "@/types/helloCashflowDomain/demoMode";
import type { UsageAnalyticsCustomerPoint } from "@/types/usageAnalytics";

export const DEMO_ANON_USER_ID_PREFIX = "demo-anon:";
export const DEMO_OPS_SENSITIVE_PERMISSION_KEY = "analytics.view_sensitive";
export const DEMO_OPS_REDACTED_VALUE = "[redacted]";
const DEMO_OPS_REDACTED_NAME = "User (restricted)";

const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
const DEMO_DEFAULT_WINDOW_DAYS = 29;

const formatDateInput = (date: Date): string =>
  date.toISOString().split("T")[0];

export interface DemoOpsUsageFilters {
  startDate: string;
  endDate: string;
  organisationId: string;
}

export interface DemoUserUsageSummary {
  userId: string;
  activeMs: number;
  activeMinutes: number;
  sliceCount: number;
  lastUsageDate: string | null;
}

export interface DemoAnonUsageSummary {
  sessionCount: number;
  totalActiveMinutes: number;
}

export interface DemoAccessRow {
  id: string;
  displayName: string;
  email: string;
  firebaseId: string;
  demoAccessEnabled: boolean;
  grantedBy: string;
  updatedAt: string | null;
  activeMinutes: number;
  lastUsageDate: string | null;
}

export const buildDefaultDemoOpsFilters = (
  now: Date = new Date(),
): DemoOpsUsageFilters => ({
  startDate: formatDateInput(
    new Date(now.getTime() - DEMO_DEFAULT_WINDOW_DAYS * DAY_IN_MILLISECONDS),
  ),
  endDate: formatDateInput(now),
  organisationId: DEMO_USAGE_ORG_ID,
});

export const hasDemoOpsSensitiveAccess = (
  permissionKeys: readonly string[],
): boolean => permissionKeys.includes(DEMO_OPS_SENSITIVE_PERMISSION_KEY);

const shouldRedactDisplayName = (
  displayName: string,
  assignment: Pick<DemoAccessAssignment, "email" | "firebaseId">,
): boolean => {
  const normalizedDisplayName = displayName.trim().toLowerCase();
  if (!normalizedDisplayName) {
    return true;
  }

  const normalizedEmail = assignment.email.trim().toLowerCase();
  return (
    normalizedDisplayName === normalizedEmail ||
    displayName === assignment.firebaseId
  );
};

export const buildUserUsageSummaries = (
  points: UsageAnalyticsCustomerPoint[],
): Map<string, DemoUserUsageSummary> => {
  const byUser = new Map<string, DemoUserUsageSummary>();

  for (const point of points) {
    const existing = byUser.get(point.userId) ?? {
      userId: point.userId,
      activeMs: 0,
      activeMinutes: 0,
      sliceCount: 0,
      lastUsageDate: null,
    };

    const activeMs = existing.activeMs + point.activeMs;
    const sliceCount = existing.sliceCount + point.sessionCount;
    const lastUsageDate =
      !existing.lastUsageDate || point.usageDate > existing.lastUsageDate
        ? point.usageDate
        : existing.lastUsageDate;

    byUser.set(point.userId, {
      ...existing,
      activeMs,
      activeMinutes: activeMs / 60000,
      sliceCount,
      lastUsageDate,
    });
  }

  return byUser;
};

export const buildAnonUsageSummary = (
  usageByUserId: Map<string, DemoUserUsageSummary>,
): DemoAnonUsageSummary => {
  const anonRows = Array.from(usageByUserId.values()).filter((row) =>
    row.userId.startsWith(DEMO_ANON_USER_ID_PREFIX),
  );

  const totalActiveMs = anonRows.reduce((acc, row) => acc + row.activeMs, 0);

  return {
    sessionCount: anonRows.length,
    totalActiveMinutes: totalActiveMs / 60000,
  };
};

export const buildDemoAccessRows = (
  assignments: DemoAccessAssignment[] | undefined,
  usageByUserId: Map<string, DemoUserUsageSummary>,
  options?: {
    includeSensitiveIdentifiers?: boolean;
  },
): DemoAccessRow[] => {
  const list = assignments ?? [];
  const includeSensitiveIdentifiers =
    options?.includeSensitiveIdentifiers ?? false;

  return list.map((assignment, index) => {
    const usageSummary = includeSensitiveIdentifiers
      ? (usageByUserId.get(assignment.firebaseId) ?? null)
      : null;
    const rawDisplayName = assignment.displayName.trim();
    const displayName =
      includeSensitiveIdentifiers ||
      !shouldRedactDisplayName(rawDisplayName, assignment)
        ? rawDisplayName
        : DEMO_OPS_REDACTED_NAME;

    return {
      id: includeSensitiveIdentifiers
        ? assignment.firebaseId
        : (assignment.assignmentKey ?? `demo-access-assignment-${index + 1}`),
      displayName,
      email: includeSensitiveIdentifiers
        ? assignment.email
        : DEMO_OPS_REDACTED_VALUE,
      firebaseId: includeSensitiveIdentifiers
        ? assignment.firebaseId
        : DEMO_OPS_REDACTED_VALUE,
      demoAccessEnabled: assignment.demoAccessEnabled,
      grantedBy: includeSensitiveIdentifiers
        ? (assignment.grantedBy ?? "—")
        : "—",
      updatedAt: assignment.updatedAt,
      activeMinutes: usageSummary ? Math.round(usageSummary.activeMinutes) : 0,
      lastUsageDate: usageSummary?.lastUsageDate ?? null,
    };
  });
};

export const sortDemoAccessRows = (rows: DemoAccessRow[]): DemoAccessRow[] => {
  return [...rows].sort((left, right) => {
    if (left.demoAccessEnabled !== right.demoAccessEnabled) {
      return left.demoAccessEnabled ? -1 : 1;
    }

    if (left.activeMinutes !== right.activeMinutes) {
      return right.activeMinutes - left.activeMinutes;
    }

    const leftLastUsage = left.lastUsageDate ?? "";
    const rightLastUsage = right.lastUsageDate ?? "";
    if (leftLastUsage !== rightLastUsage) {
      return rightLastUsage.localeCompare(leftLastUsage);
    }

    return left.displayName.localeCompare(right.displayName, undefined, {
      sensitivity: "base",
    });
  });
};

export const filterDemoAccessRows = (
  rows: DemoAccessRow[],
  options?: {
    includeInactiveAssignments?: boolean;
  },
): DemoAccessRow[] => {
  if (options?.includeInactiveAssignments) {
    return rows;
  }

  return rows.filter((row) => row.demoAccessEnabled);
};
